import AngleLeftSvg from "@icons/angle-left.svg?react";
import UsdCircleSvg from "@icons/usd-circle.svg?react";
import { t } from "@lingui/core/macro";
import { Trans } from "@lingui/react/macro";
import { useQuery } from "@tanstack/react-query";
import { isNumber } from "lodash-es";
import { FormProvider, useForm } from "react-hook-form";
import {
  Outlet,
  useLocation,
  useNavigate,
  useOutletContext,
} from "react-router-dom";
import { z } from "zod";

import {
  cropUnitSchema,
  traderSchema as baseTraderSchema,
} from "@/api/types/digitalnisklady.cz/types";
import { BindingOfferBuyDialog } from "@/common/components/binding-offer-dialog/binding-offer-buy-dialog";
import { Button } from "@/common/components/button/button";
import { InputField } from "@/common/components/form/input-field/input-field";
import { PropertiesList } from "@/common/components/properties-list/properties-list";
import { Spacer } from "@/common/components/spacer/spacer";
import { useCurrency } from "@/common/hooks/use-currency";
import { useRequiredErpCompany } from "@/common/hooks/use-erp-company";
import { entitySchema } from "@/common/schema/entity";
import {
  currencyFormatter,
  numberFormatter,
} from "@/common/services/formatter";
import {
  AmountDiscountValueType,
  CropCategory,
  Currency,
} from "@/generated/digitalnisklady.cz/graphql";
import { FullscreenModalLayout } from "@/routes/layout/layout.fullscreen";
import { getUnit } from "@/routes/shared/utils/crop-unit";

import { useConfirmedAction } from "../../admin/components/user/user";
import { useGoToFailed } from "../../shared/routes/failed/failed";
import { formatDiscount } from "./discount-formatter";
import {
  marketPriceQuery,
  useAddMaterialContractMutation,
} from "./marketplace.detail.api";

type Fields = {
  amount: number;
  note: string;
};

const MarketplaceDetailBuy = () => {
  const context = useRouteContext();
  const company = useRequiredErpCompany();
  const unit = getUnit(context.trader.unit);
  const [, currency] = useCurrency();
  const {
    postalCodeLabel,
    trader: { minimumAmount },
  } = context;
  const formattedMinimumAmount = isNumber(minimumAmount)
    ? numberFormatter().format(minimumAmount)
    : 0;
  const close = useClose();
  const form = useForm<Fields>();
  const addMaterialContract = useAddMaterialContractMutation();
  const goToFailedScreen = useGoToFailed();
  const goToSuccessScreen = useGoToSuccess();
  const { runWhenConfirmed, dialog, setDialog, rejectRef, resolveRef } =
    useConfirmedAction();
  const amount = form.watch("amount");

  // Fetch dynamic price based on amount
  const { data: dynamicPrice } = useQuery(
    marketPriceQuery({
      amount: amount,
      currency: currency as Currency,
      id: context.marketPriceId,
    }),
  );

  const totalPrice =
    isFinite(amount) && dynamicPrice ? amount * dynamicPrice : undefined;
  const totalPriceParameter = {
    label: t`Celková cena`,
    value: totalPrice ? currencyFormatter(currency).format(totalPrice) : "-",
  };

  const handleSubmit = (data: Fields) => {
    addMaterialContract.mutate(
      {
        ...data,
        companyId: company.id,
        cropId: context.item.id,
        currency: currency as Currency,
        gps: {
          latitude: context.latitude,
          longitude: context.longitude,
        },
        marketPriceId: context.marketPriceId,
        zip: context.postalCode,
      },
      {
        onError: async (error) => {
          setDialog(undefined);
          await goToFailedScreen(error);
        },
        onSuccess: async (data) => {
          setDialog(undefined);
          await goToSuccessScreen(data.addFastContract!.id!);
        },
      },
    );
  };

  return (
    <>
      <FullscreenModalLayout
        headerButton={
          <button onClick={close}>
            <AngleLeftSvg />
          </button>
        }
      >
        <FullscreenModalLayout.Title>
          <Trans>Nakoupit</Trans>
        </FullscreenModalLayout.Title>
        <PropertiesList
          properties={[...context.item.parameters, totalPriceParameter]}
        />
        <Spacer />
        {context.trader.discounts.length > 0 ? (
          <>
            <div className="flex flex-col gap-2">
              <span className="text-base flex items-center font-bold text-can-forest-teal gap-2">
                <UsdCircleSvg className="[&_path]:fill-can-forest-teal w-4 h-4" />
                <Trans>Množstevní slevy</Trans>
              </span>
              {context.trader.discounts.map((discount) => (
                <span className="text-base" key={discount.id}>
                  {formatDiscount(
                    discount,
                    context.price,
                    discount.currency ?? currency,
                    unit,
                    context.category,
                  )}
                </span>
              ))}
            </div>
            <Spacer />
          </>
        ) : null}
        <FormProvider {...form}>
          <form
            className="flex flex-col gap-y-6"
            id="marketplace-detail-buy"
            onSubmit={form.handleSubmit(handleSubmit)}
          >
            <InputField<Fields>
              label={t`Množství` + " (" + unit + ")"}
              name="amount"
              options={{
                min: {
                  message: t`Množství musí být vyšší než ${formattedMinimumAmount} ${unit}`,
                  value: minimumAmount ?? 0,
                },
                required: t`Vyplňte prosím množství`,
                valueAsNumber: true,
              }}
              type="number"
            />
            <InputField<Fields> label={t`Poznámka`} name="note" />
          </form>
        </FormProvider>
        <Button.Container>
          <Button
            form="marketplace-detail-buy"
            onClick={async () => {
              const isValid = await form.trigger();
              if (isValid) {
                runWhenConfirmed(
                  <Trans>
                    Přejete si vytvořit závaznou objednávku a materiál nakoupit?
                  </Trans>,
                  () => {
                    const formData = form.getValues();
                    handleSubmit(formData);
                  },
                )();
              }
            }}
            type="button"
            variant="primary"
          >
            <Trans>Nakoupit za</Trans>{" "}
            {dynamicPrice
              ? currencyFormatter(currency).format(dynamicPrice)
              : currencyFormatter(currency).format(context.price)}
          </Button>
        </Button.Container>
      </FullscreenModalLayout>
      <Outlet />
      {dialog ? (
        <BindingOfferBuyDialog
          amount={form.getValues("amount") + " " + unit}
          cropName={context.item.name}
          isPending={addMaterialContract.status === "pending"}
          onCancel={() => rejectRef.current?.()}
          onConfirm={(e) => resolveRef.current?.(e)}
          postalCodeLabel={postalCodeLabel}
          quarter={context.quarterName ?? ""}
          timeFromTrading={context.trader.timeFromTrading}
          timeToTrading={context.trader.timeToTrading}
          tocUrl={context.trader.tocUrl}
          traderName={context.trader.name}
          unitPrice={currencyFormatter(currency).format(
            dynamicPrice ?? context.price,
          )}
        />
      ) : null}
    </>
  );
};

const useClose = () => {
  const navigate = useNavigate();
  const { search } = useLocation();

  return async () => {
    await navigate(
      {
        pathname: "..",
        search,
      },
      {
        relative: "path",
      },
    );
  };
};

const discountSchema = z.object({
  currency: z.enum(Currency).or(z.null()),
  fromAmount: z.number(),
  id: z.string(),
  toAmount: z.number(),
  type: z.string(),
  value: z.number(),
  valueType: z.enum(AmountDiscountValueType),
});

const traderSchema = baseTraderSchema.extend(
  z.object({
    discounts: z.array(discountSchema),
    minimumAmount: z.number().optional().nullable(),
    unit: cropUnitSchema,
  }).shape,
);

const itemSchema = entitySchema.extend(
  z.object({
    parameters: z.array(
      z.object({
        label: z.string(),
        value: z.string().or(z.number()).or(z.custom<React.ReactElement>()),
      }),
    ),
  }).shape,
);

const contextSchema = z.object({
  category: z.enum(CropCategory),
  item: itemSchema,
  latitude: z.number(),
  longitude: z.number(),
  marketPriceId: z.string(),
  postalCode: z.string(),
  postalCodeLabel: z.string(),
  price: z.number(),
  quarterName: z.string().optional(),
  trader: traderSchema,
});

const useRouteContext = () => {
  const [, currency] = useCurrency();
  const context = useOutletContext();
  const safeContext = contextSchema.parse(context);

  if (safeContext.trader.minimumAmount) {
    safeContext.item.parameters.unshift({
      label: t`Minimální množství`,
      value: safeContext.trader.minimumAmount,
    });
  }

  safeContext.item.parameters = [
    {
      label: t`Název`,
      value: safeContext.item.name,
    },
    ...(safeContext.trader.minimumAmount
      ? [
          {
            label: t`Minimální odběr`,
            value:
              safeContext.trader.minimumAmount +
              getUnit(safeContext.trader.unit),
          },
        ]
      : []),
    {
      label: t`Jednotková cena`,
      value: currencyFormatter(currency).format(safeContext.price),
    },
    {
      label: t`Prodejce`,
      value: <img src={safeContext.trader.logo} style={{ width: "94px" }} />,
    },
    ...safeContext.item.parameters,
  ];

  return safeContext;
};

const useGoToSuccess = () => {
  const navigate = useNavigate();
  const { search } = useLocation();

  return async (contractId: string) => {
    await navigate({
      pathname: "./" + contractId + "/success",
      search,
    });
  };
};

export { MarketplaceDetailBuy };
